import { useState } from "react";
import type { OpenProjectRosterPreview } from "@payday/contracts";
import { Button } from "../payroll/components/ui/Button.js";
import { Input } from "../payroll/components/ui/Input.js";

export function RosterImportPreview({
  preview,
  busy,
  onApply,
}: {
  preview: OpenProjectRosterPreview;
  busy: boolean;
  onApply: () => void;
}) {
  const [search, setSearch] = useState("");
  const [page, setPage] = useState(1);
  const query = search.trim().toLocaleLowerCase();
  const people = [...preview.people]
    .sort((a, b) => a.displayName.localeCompare(b.displayName))
    .filter(
      (person) =>
        !query ||
        [
          person.displayName,
          person.username,
          String(person.openProjectUserId),
        ].some((value) => value.toLocaleLowerCase().includes(query)),
    );
  const pageCount = Math.max(1, Math.ceil(people.length / 10));
  const currentPage = Math.min(page, pageCount);
  const offset = (currentPage - 1) * 10;
  const visible = people.slice(offset, offset + 10);
  return (
    <section className="mt-5 space-y-3" aria-label="Roster import preview">
      <header className="flex flex-wrap items-center justify-between gap-2">
        <h4 className="font-semibold">Review import</h4>
        <span>{preview.people.length} selected people</span>
      </header>
      <dl className="flex flex-wrap gap-x-6 gap-y-2 text-sm">
        {[
          ["Retained", preview.retained],
          ["Added", preview.added],
          ["Archived", preview.archived],
        ].map(([label, count]) => (
          <div key={label} className="flex items-baseline gap-2">
            <dt>{label}</dt>
            <dd className="font-semibold tabular-nums">{count}</dd>
          </div>
        ))}
      </dl>
      <Input
        type="search"
        aria-label="Search imported people"
        placeholder="Search selected people"
        value={search}
        onChange={(event) => {
          setSearch(event.target.value);
          setPage(1);
        }}
      />
      <div className="overflow-hidden rounded-lg border border-border">
        <table
          className="w-full table-fixed text-left text-sm"
          aria-label="Selected people"
        >
          <thead className="bg-elevated">
            <tr>
              <th className="w-3/5 px-3 py-2 font-medium" scope="col">
                Name
              </th>
              <th className="px-3 py-2 font-medium" scope="col">
                Username
              </th>
            </tr>
          </thead>
          <tbody>
            {visible.length ? (
              visible.map((person) => (
                <tr
                  key={person.openProjectUserId}
                  className="border-t border-border"
                >
                  <td className="break-words px-3 py-2">
                    {person.displayName}
                  </td>
                  <td className="break-all px-3 py-2">{person.username}</td>
                </tr>
              ))
            ) : (
              <tr>
                <td className="px-3 py-4" colSpan={2}>
                  No selected people match this search.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
      <nav
        className="flex flex-wrap items-center justify-between gap-2"
        aria-label="Import preview pages"
      >
        <span className="text-sm tabular-nums" aria-live="polite">
          {people.length ? offset + 1 : 0}-{offset + visible.length} of{" "}
          {people.length}
        </span>
        <div className="flex gap-2">
          <Button
            variant="outline"
            size="sm"
            aria-label="Previous people"
            disabled={currentPage === 1}
            onClick={() => setPage(currentPage - 1)}
          >
            Previous
          </Button>
          <Button
            variant="outline"
            size="sm"
            aria-label="Next people"
            disabled={currentPage === pageCount}
            onClick={() => setPage(currentPage + 1)}
          >
            Next
          </Button>
        </div>
      </nav>
      <div className="flex flex-wrap items-center justify-between gap-3 border-t border-border pt-3">
        <p className="max-w-prose text-sm">
          Applying this file replaces the roster. Archived people keep their
          payroll history and contract terms.
        </p>
        <Button disabled={busy} onClick={onApply}>
          Apply reviewed roster
        </Button>
      </div>
    </section>
  );
}
